/**
 * @fileoverview Input/output schemas for character-ideation-v2.
 *
 * This agent generates a list of major and minor character ideas.
 *
 * Storylines (with beats and questions) are pulled from project state via the
 * `existingStorylines` accessor. When present, the prompt renders them grouped
 * by storyline for structurally-aware character ideation. When absent, the
 * agent relies on storyContext (which includes writtenPlot).
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { CharacterV2IdeaSchema } from '@/domains/characters/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.initialChapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.bannedNames.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.projectedWordCount.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.existingStorylines.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  characters: z.array(CharacterV2IdeaSchema).describe('An array of generated character ideas.'),
});

export type Output = z.infer<typeof OutputSchema>;
